import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Row, Col, Tag, Tooltip } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  SafetyCertificateOutlined,
  UnlockOutlined,
  CheckCircleOutlined,
  RightOutlined,
  DownOutlined,
  UpOutlined,
  TeamOutlined,
  ArrowLeftOutlined,
  ShopOutlined,
  InfoCircleOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
  DEFAULT_HEALTH_INPUTS,
  INPUT_PRESET_CHIPS,
  INPUT_LIMITS,
  LOCATION_OPTIONS,
  computeHealthMetrics,
  pickPortrait,
  fillPortraitTokens,
  getLevers,
  getAlerts,
  getPnlData,
  getBenchmarkMetrics,
  fmtNumber,
  fmtTrieu,
  fmtPercent,
} from '../data/pharmacyHealthData.js';
import {
  PROVINCES,
  getWardsByProvinceCode,
  getProvinceCodeByName,
} from '../../../constants/vietnamAdministrativeDivisions.js';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { savePendingLead, clearPendingLead, resendPendingLead } from '../../../utils/pendingLead.js';
import '../styles/pharmacy-health-check.css';

const LOCAL_STORAGE_LEAD_KEY = 'thethao_health_lead';
// Lead gửi lỗi được giữ tạm và tự gửi lại khi mở lại công cụ (YC-TOOL-09, HT-06 D-04)
const LOCAL_STORAGE_PENDING_LEAD = 'radar_pending_lead';
const LOCAL_STORAGE_INPUTS_KEY = 'thethao_health_inputs';
const SVG_CIRCUMFERENCE = 2 * Math.PI * 63; // ~395.84

export function PharmacyHealthCheckPage() {
  // 1. Quản lý trạng thái nhập số liệu (7 cốt lõi + 5 mở rộng - Lưu nháp chẩn đoán tại localStorage)
  const [inputs, setInputs] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_INPUTS_KEY);
      if (saved) {
        return { ...DEFAULT_HEALTH_INPUTS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('Lỗi đọc inputs từ localStorage:', e);
    }
    return DEFAULT_HEALTH_INPUTS;
  });
  const [isAdvOpen, setIsAdvOpen] = useState(false);

  // 2. Quản lý trạng thái form mở khóa (Lead Gate)
  const [leadForm, setLeadForm] = useState({
    shop: '',
    name: '',
    phone: '',
    province: '',
    district: '',
    address: '',
  });
  const [formError, setFormError] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const reportRef = useRef(null);

  // Xuất PDF thật (chữ chọn được, tìm kiếm được, nhẹ) bằng đúng cơ chế In của trình duyệt
  // thay vì chụp ảnh DOM rồi nhét vào PDF (cách cũ tạo ra file toàn hình ảnh, không phải văn bản thật).
  // CSS @media print trong pharmacy-health-check.css lo phần "chỉ in #detailed-report".
  const handleExportPdf = () => {
    if (!reportRef.current) return;
    toast.info({
      content: 'Ở hộp thoại In vừa mở, chọn máy in "Save as PDF" / "Lưu dưới dạng PDF" để tải file.',
      duration: 5,
    });
    window.print();
  };

  // Lấy mã tỉnh/thành theo tên (hỗ trợ cả chuẩn hóa tên cũ nếu có)
  const selectedProvinceCode = useMemo(() => {
    if (!leadForm.province) return undefined;
    const directCode = getProvinceCodeByName(leadForm.province);
    if (directCode) return directCode;
    const clean = leadForm.province.replace(/^(TP\.|Tỉnh|Thành phố)\s+/i, '').trim().toLowerCase();
    const found = PROVINCES.find((p) => {
      const pClean = p.name.toLowerCase();
      return pClean === clean || pClean.includes(clean) || clean.includes(pClean);
    });
    return found ? found.code : undefined;
  }, [leadForm.province]);

  // Danh sách Phường / Xã trực thuộc Tỉnh/Thành đã chọn (theo ĐVHC mới 2025)
  const wardOptions = useMemo(() => {
    if (!selectedProvinceCode) return [];
    return getWardsByProvinceCode(selectedProvinceCode);
  }, [selectedProvinceCode]);

  // Xử lý đổi Tỉnh / Thành phố -> reset Phường / Xã
  const handleProvinceChange = (e) => {
    const nextProvince = e.target.value;
    setLeadForm((prev) => ({
      ...prev,
      province: nextProvince,
      district: '',
    }));
  };

  // Kiểm tra nếu đã từng mở khóa trên máy này
  // Mở công cụ: nếu còn lead gửi lỗi lần trước thì gửi lại (chạy ngầm)
  useEffect(() => {
    resendPendingLead(LOCAL_STORAGE_PENDING_LEAD, (p) => pharmacyToolApi.submitHealthCheckLead(p));
  }, []);

  useEffect(() => {
    try {
      const savedLead = localStorage.getItem(LOCAL_STORAGE_LEAD_KEY);
      if (savedLead) {
        const parsed = JSON.parse(savedLead);
        if (parsed.phone && parsed.shop) {
          setLeadForm((prev) => ({
            ...prev,
            ...parsed,
          }));
          setIsUnlocked(true);
        }
      }
    } catch (e) {
      console.warn('Lỗi đọc lead từ localStorage:', e);
    }
  }, []);

  // Tính toán chỉ số Realtime
  const metrics = useMemo(() => {
    return computeHealthMetrics(inputs);
  }, [inputs]);

  // Chân dung quầy phù hợp nhất
  const { portrait, sev } = useMemo(() => {
    return pickPortrait(metrics);
  }, [metrics]);

  // Các dữ liệu dẫn xuất cho báo cáo
  const leversData = useMemo(() => getLevers(metrics), [metrics]);
  const alertsData = useMemo(() => getAlerts(metrics), [metrics]);
  const pnlData = useMemo(() => getPnlData(metrics), [metrics]);
  const benchmarkMetrics = useMemo(() => getBenchmarkMetrics(metrics), [metrics]);

  // Chỉ giữ lại chữ số và tối đa 1 dấu chấm thập phân khi gõ - chặn số âm, chữ cái, "e", nhiều dấu chấm...
  // ngay từ lúc gõ, không đợi tới lúc tính toán mới lọc.
  const sanitizeNumericTyping = (raw) => {
    let v = String(raw).replace(/[^0-9.]/g, '');
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
    }
    return v;
  };

  // Tự động lưu nháp số liệu chẩn đoán vào localStorage
  const handleInputChange = (field, val) => {
    const sanitized = INPUT_LIMITS[field] ? sanitizeNumericTyping(val) : val;
    setInputs((prev) => {
      const updated = { ...prev, [field]: sanitized };
      try {
        localStorage.setItem(LOCAL_STORAGE_INPUTS_KEY, JSON.stringify(updated));
      } catch (e) { }
      return updated;
    });
  };

  // Khi rời khỏi ô nhập (blur): chốt lại đúng khoảng giá trị hợp lệ của từng ô,
  // tránh trường hợp số hiển thị trên UI (vd gõ nhầm 999999) khác với số thực tế dùng để tính toán.
  const handleInputBlur = (field) => {
    const limits = INPUT_LIMITS[field];
    if (!limits) return;
    setInputs((prev) => {
      const parsed = parseFloat(prev[field]);
      const safe = Number.isFinite(parsed) ? parsed : limits.min;
      const clamped = Math.min(limits.max, Math.max(limits.min, safe));
      const updated = { ...prev, [field]: clamped };
      try {
        localStorage.setItem(LOCAL_STORAGE_INPUTS_KEY, JSON.stringify(updated));
      } catch (e) { }
      return updated;
    });
  };

  // Đặt lại toàn bộ số liệu đã nhập về mặc định (xóa nháp đang lưu ở localStorage)
  const handleResetInputs = () => {
    Modal.confirm({
      title: 'Đặt lại toàn bộ số liệu đã nhập?',
      icon: <ExclamationCircleOutlined style={{ color: '#d97706' }} />,
      content: 'Các số liệu bạn vừa nhập sẽ bị xóa và trở về số mẫu ban đầu. Thao tác này không thể hoàn tác.',
      okText: 'Đặt lại',
      cancelText: 'Hủy',
      onOk: () => {
        setInputs(DEFAULT_HEALTH_INPUTS);
        try {
          localStorage.removeItem(LOCAL_STORAGE_INPUTS_KEY);
        } catch (e) { }
        toast.info({ content: 'Đã đặt lại số liệu về mặc định.', duration: 2 });
      },
    });
  };

  // Click vào các chip số gợi ý
  const handleChipClick = (field, val) => {
    setInputs((prev) => {
      const updated = { ...prev, [field]: val };
      try {
        localStorage.setItem(LOCAL_STORAGE_INPUTS_KEY, JSON.stringify(updated));
      } catch (e) { }
      return updated;
    });
  };

  // Submit mở khóa báo cáo chi tiết
  const handleUnlockReport = async (e) => {
    if (e) e.preventDefault();
    setFormError('');

    const shop = leadForm.shop.trim();
    const name = leadForm.name.trim();
    const phone = leadForm.phone.replace(/[^0-9]/g, '');
    const province = leadForm.province;
    const district = (leadForm.district || '').trim();
    const address = leadForm.address.trim();

    // Validation
    if (shop.length < 2) {
      setFormError('Vui lòng nhập tên quầy thuốc / nhà thuốc của bạn.');
      return;
    }
    if (name.length < 2) {
      setFormError('Vui lòng nhập tên người phụ trách hoặc chủ quầy.');
      return;
    }
    if (!/^0\d{9}$/.test(phone)) {
      setFormError('Số điện thoại/Zalo không hợp lệ (cần đủ 10 số và bắt đầu bằng số 0).');
      return;
    }
    if (!province) {
      setFormError('Vui lòng chọn Tỉnh hoặc Thành phố nơi quầy hoạt động.');
      return;
    }
    if (wardOptions.length > 0 && !district) {
      setFormError('Vui lòng chọn Phường hoặc Xã nơi quầy hoạt động.');
      return;
    }
    if (address.length < 3) {
      setFormError('Vui lòng nhập địa chỉ cụ thể của quầy (số nhà, tên đường).');
      return;
    }

    setIsSubmittingLead(true);

    const leadData = {
      shop,
      name,
      phone,
      province,
      district,
      address,
    };

    // Lưu vào LocalStorage
    try {
      localStorage.setItem(LOCAL_STORAGE_LEAD_KEY, JSON.stringify(leadData));
    } catch (err) {
      console.warn('Lỗi lưu lead vào localStorage:', err);
    }

    // Gửi dữ liệu Lead lên Backend
    let leadPayload = null;
    try {
      const fullAddress = district ? `${address}, ${district}` : address;

      const round1 = (v) => (Number.isFinite(v) ? Math.round(v * 10) / 10 : 0);
      leadPayload = {
        pharmacyName: shop,
        contactName: name,
        phone,
        province,
        district,
        address: fullAddress,
        // Kết quả đo để đội tư vấn biết quầy đang ở mức nào (HT-06 D-04)
        score: Math.max(0, Math.min(100, Math.round(metrics.total || 0))),
        ranking: metrics.noSalary ? 'Thiếu dữ liệu' : metrics.band?.tag || '',
        portrait: portrait?.ten || '',
        metrics: {
          doanhThuThang: Math.max(0, round1(metrics.dt)),
          bienLaiGop: Math.max(0, Math.min(100, round1(metrics.d?.bien))),
          loiNhuanThang: round1(metrics.ln),
          ngayTon: Math.max(0, Math.min(10000, round1(metrics.ngayTon))),
        },
      };
      await pharmacyToolApi.submitHealthCheckLead(leadPayload);
      clearPendingLead(LOCAL_STORAGE_PENDING_LEAD);
    } catch (error) {
      console.warn('Backend chưa sẵn sàng lưu lead, tiếp tục mở khóa offline:', error);
      if (leadPayload && error?.statusCode !== 400) savePendingLead(LOCAL_STORAGE_PENDING_LEAD, leadPayload);
    } finally {
      setIsSubmittingLead(false);
      setIsUnlocked(true);
      toast.success('Đã mở khóa toàn bộ báo cáo chuyên sâu cho quầy!');

      // Cuộn xuống báo cáo
      setTimeout(() => {
        if (reportRef.current) {
          reportRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  };

  // Tính toán stroke offset cho vòng tròn Dial SVG
  const strokeDashoffset = useMemo(() => {
    const p = Math.max(0, Math.min(100, metrics.total));
    return SVG_CIRCUMFERENCE * (1 - p / 100);
  }, [metrics.total]);

  return (
    <div className="pharmacy-tool-wrapper radar-wrapper">
      {/* Hero Banner Header - Đồng bộ 100% với website và các công cụ khác */}
      <div className="tool-hero-banner">
        <div className="main-container" style={{ maxWidth: 1080, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ marginBottom: 14 }}>
            <Link
              to="/tools"
              style={{
                color: '#d1fae5',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.12)',
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeftOutlined /> Trung Tâm Công Cụ Thực Chiến
            </Link>
          </div>

          <Row gutter={[24, 24]} align="middle" justify="space-between">
            <Col xs={24} md={16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Tag color="#10b981" style={{ fontWeight: 700, borderRadius: 4, padding: '2px 8px' }}>
                  ĐO LƯỜNG SỨC KHỎE NHÀ THUỐC
                </Tag>
                <span style={{ fontSize: 13, color: '#a7f3d0' }}>Tự Kiểm Tra Hiệu Quả Kinh Doanh · 3 Phút</span>
              </div>
              <h1 className="tool-hero-title" style={{ fontSize: 'clamp(24px, 4vw, 32px)', lineHeight: 1.25, marginBottom: 10 }}>
                Quầy của bạn đang <span style={{ color: '#fde047' }}>lãi thật</span> hay chỉ đang quay vòng tiền?
              </h1>
              <p className="tool-hero-subtitle" style={{ fontSize: 14.5, lineHeight: 1.6, maxWidth: 740, marginBottom: 0 }}>
                Nhập 7 con số cơ bản, công cụ tính ra 6 chỉ số tài chính, so với ngưỡng an toàn của ngành dược bán lẻ,
                xác định quầy bạn thuộc chân dung nào và chỉ đúng chỗ đang rò rỉ dòng tiền.
              </p>
            </Col>

            <Col xs={24} md={8}>
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 12,
                  padding: '16px 18px',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  color: '#ffffff',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontWeight: 700, fontSize: 14 }}>
                  <SafetyCertificateOutlined style={{ color: '#fde047', fontSize: 18 }} />
                  <span>Bảo Mật 100% & Ẩn Danh</span>
                </div>
                <div style={{ fontSize: 12.5, color: '#d1fae5', lineHeight: 1.5 }}>
                  Số liệu tính toán trực tiếp trên trình duyệt — không gửi lên server, không lưu lại. Bạn không cần đăng nhập hay để lại thông tin để xem kết quả sơ bộ.
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div className="radar-container" style={{ marginTop: 8 }}>
        {/* BẢNG NHẬP SỐ LIỆU QUẦY THUỐC */}
        <div className="radar-panel">
          <div className="radar-phead">
            <div className="radar-phead-top">
              <h2 className="radar-phead-title">
                <ShopOutlined style={{ color: 'var(--ttp-primary)' }} />
                Số liệu kinh doanh quầy thuốc
              </h2>
              <button type="button" className="radar-reset-btn" onClick={handleResetInputs}>
                <ReloadOutlined />
                <span>Đặt lại</span>
              </button>
            </div>
            <span className="radar-phead-note">Đang hiển thị số mẫu — chỉnh sửa theo quầy của bạn</span>
          </div>

          <div className="radar-pbody">
            <div className="radar-form-grid">
              {/* 1. Doanh số trung bình 1 ngày */}
              <div className="radar-field">
                <label className="radar-label" htmlFor="f-ds">
                  <span>1. Doanh số trung bình một ngày</span>
                </label>
                <div className="radar-input-row">
                  <input
                    id="f-ds"
                    type="number"
                    step="0.1"
                    min={INPUT_LIMITS.ds.min}
                    max={INPUT_LIMITS.ds.max}
                    value={inputs.ds}
                    onChange={(e) => handleInputChange('ds', e.target.value)}
                    onBlur={() => handleInputBlur('ds')}
                  />
                  <span className="radar-input-unit">triệu đ</span>
                </div>
                <div className="radar-chips">
                  {INPUT_PRESET_CHIPS.ds.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      className={`radar-chip-btn ${parseFloat(inputs.ds) === chip.value ? 'active' : ''}`}
                      onClick={() => handleChipClick('ds', chip.value)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                <div className="radar-hint">Tính theo tháng 30 ngày mở cửa bán hàng.</div>
              </div>

              {/* 2. Biên lãi gộp trung bình */}
              <div className="radar-field">
                <label className="radar-label" htmlFor="f-bien">
                  <span>2. Biên lãi gộp trung bình</span>
                </label>
                <div className="radar-input-row">
                  <input
                    id="f-bien"
                    type="number"
                    step="0.5"
                    min={INPUT_LIMITS.bien.min}
                    max={INPUT_LIMITS.bien.max}
                    value={inputs.bien}
                    onChange={(e) => handleInputChange('bien', e.target.value)}
                    onBlur={() => handleInputBlur('bien')}
                  />
                  <span className="radar-input-unit">%</span>
                </div>
                <div className="radar-hint">
                  Bán 100.000đ, sau khi trừ giá nhập hàng còn lại bao nhiêu. Quầy lẻ thường ở mức 18–28%.
                </div>
              </div>

              {/* 3. Đơn giá trung bình mỗi khách */}
              <div className="radar-field">
                <label className="radar-label" htmlFor="f-dongia">
                  <span>3. Đơn giá trung bình mỗi khách</span>
                </label>
                <div className="radar-input-row">
                  <input
                    id="f-dongia"
                    type="number"
                    step="1"
                    min={INPUT_LIMITS.dongia.min}
                    max={INPUT_LIMITS.dongia.max}
                    value={inputs.dongia}
                    onChange={(e) => handleInputChange('dongia', e.target.value)}
                    onBlur={() => handleInputBlur('dongia')}
                  />
                  <span className="radar-input-unit">nghìn đ</span>
                </div>
                <div className="radar-chips">
                  {INPUT_PRESET_CHIPS.dongia.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      className={`radar-chip-btn ${parseFloat(inputs.dongia) === chip.value ? 'active' : ''}`}
                      onClick={() => handleChipClick('dongia', chip.value)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 4. Thuê mặt bằng 1 tháng */}
              <div className="radar-field">
                <label className="radar-label" htmlFor="f-mb">
                  <span>4. Thuê mặt bằng một tháng</span>
                </label>
                <div className="radar-input-row">
                  <input
                    id="f-mb"
                    type="number"
                    step="0.5"
                    min={INPUT_LIMITS.mb.min}
                    max={INPUT_LIMITS.mb.max}
                    value={inputs.mb}
                    onChange={(e) => handleInputChange('mb', e.target.value)}
                    onBlur={() => handleInputBlur('mb')}
                  />
                  <span className="radar-input-unit">triệu đ</span>
                </div>
                <div className="radar-chips">
                  {INPUT_PRESET_CHIPS.mb.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      className={`radar-chip-btn ${parseFloat(inputs.mb) === chip.value ? 'active' : ''}`}
                      onClick={() => handleChipClick('mb', chip.value)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 5. Chi phí cố định khác */}
              <div className="radar-field">
                <label className="radar-label" htmlFor="f-khac">
                  <span>5. Chi phí phụ một tháng</span>
                </label>
                <div className="radar-input-row">
                  <input
                    id="f-khac"
                    type="number"
                    step="0.5"
                    min={INPUT_LIMITS.khac.min}
                    max={INPUT_LIMITS.khac.max}
                    value={inputs.khac}
                    onChange={(e) => handleInputChange('khac', e.target.value)}
                    onBlur={() => handleInputBlur('khac')}
                  />
                  <span className="radar-input-unit">triệu đ</span>
                </div>
                <div className="radar-hint">Điện, nước, internet, phần mềm, thuế khoán, phí GPP, bảo trì…</div>
              </div>

              {/* 6. Lương + thưởng nhân sự */}
              <div className="radar-field">
                <label className="radar-label" htmlFor="f-ns">
                  <span>6. Lương + thưởng nhân sự một tháng</span>
                </label>
                <div className="radar-input-row">
                  <input
                    id="f-ns"
                    type="number"
                    step="0.5"
                    min={INPUT_LIMITS.ns.min}
                    max={INPUT_LIMITS.ns.max}
                    value={inputs.ns}
                    onChange={(e) => handleInputChange('ns', e.target.value)}
                    onBlur={() => handleInputBlur('ns')}
                  />
                  <span className="radar-input-unit">triệu đ</span>
                </div>
                <div className="radar-hint">
                  <strong>Tính cả công của chính bạn.</strong> Nếu bạn tự đứng quầy, hãy cộng mức lương bạn phải trả nếu thuê người khác làm thay.
                </div>
              </div>

              {/* 7. Hàng tồn kho hiện tại */}
              <div className="radar-field">
                <label className="radar-label" htmlFor="f-ton">
                  <span>7. Giá trị hàng tồn kho hiện tại</span>
                </label>
                <div className="radar-input-row">
                  <input
                    id="f-ton"
                    type="number"
                    step="5"
                    min={INPUT_LIMITS.ton.min}
                    max={INPUT_LIMITS.ton.max}
                    value={inputs.ton}
                    onChange={(e) => handleInputChange('ton', e.target.value)}
                    onBlur={() => handleInputBlur('ton')}
                  />
                  <span className="radar-input-unit">triệu đ</span>
                </div>
                <div className="radar-chips">
                  {INPUT_PRESET_CHIPS.ton.map((chip) => (
                    <button
                      key={chip.value}
                      type="button"
                      className={`radar-chip-btn ${parseFloat(inputs.ton) === chip.value ? 'active' : ''}`}
                      onClick={() => handleChipClick('ton', chip.value)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Collapsible 5 thông tin nâng cao */}
          <div className="radar-adv-toggle" onClick={() => setIsAdvOpen(!isAdvOpen)}>
            <span>{isAdvOpen ? '– Thu gọn 3 thông tin nâng cao' : '+ Bổ sung 3 thông tin nâng cao để kết quả sát hơn'}</span>
            {isAdvOpen ? <UpOutlined /> : <DownOutlined />}
          </div>

          {isAdvOpen && (
            <div className="radar-pbody" style={{ borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
              <div className="radar-form-grid">
                <div className="radar-field">
                  <label className="radar-label">Số người làm tại quầy</label>
                  <div className="radar-input-row">
                    <input
                      type="number"
                      step="1"
                      min={INPUT_LIMITS.nhansu.min}
                      max={INPUT_LIMITS.nhansu.max}
                      value={inputs.nhansu}
                      onChange={(e) => handleInputChange('nhansu', e.target.value)}
                      onBlur={() => handleInputBlur('nhansu')}
                    />
                    <span className="radar-input-unit">người</span>
                  </div>
                  <div className="radar-hint">Tính cả bạn, quy đổi theo nhân sự làm việc toàn thời gian.</div>
                </div>

                <div className="radar-field">
                  <label className="radar-label">Hàng cận date, hỏng, hủy mỗi tháng</label>
                  <div className="radar-input-row">
                    <input
                      type="number"
                      step="0.1"
                      min={INPUT_LIMITS.huy.min}
                      max={INPUT_LIMITS.huy.max}
                      value={inputs.huy}
                      onChange={(e) => handleInputChange('huy', e.target.value)}
                      onBlur={() => handleInputBlur('huy')}
                    />
                    <span className="radar-input-unit">% doanh thu</span>
                  </div>
                  <div className="radar-hint">Nếu không rõ hãy để 0.8%. Quầy quản kho lỏng lẻo thường 2–3%.</div>
                </div>

                <div className="radar-field">
                  <label className="radar-label">Lãi vay phải trả mỗi tháng</label>
                  <div className="radar-input-row">
                    <input
                      type="number"
                      step="0.1"
                      min={INPUT_LIMITS.vay.min}
                      max={INPUT_LIMITS.vay.max}
                      value={inputs.vay}
                      onChange={(e) => handleInputChange('vay', e.target.value)}
                      onBlur={() => handleInputBlur('vay')}
                    />
                    <span className="radar-input-unit">triệu đ</span>
                  </div>
                  <div className="radar-hint">Chỉ tính tiền lãi hàng tháng, không tính phần tiền trả gốc.</div>
                </div>

              </div>
            </div>
          )}
        </div>

        {/* KẾT QUẢ SƠ BỘ (Hoàn toàn Miễn phí & Ẩn danh) */}
        <div className="radar-panel" style={{ marginTop: 24 }}>
          <div className="radar-verdict">
            {/* Đồng hồ Dial SVG */}
            <div className="radar-dial">
              <svg viewBox="0 0 148 148" width="154" height="154" aria-hidden="true">
                <circle
                  cx="74"
                  cy="74"
                  r="63"
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="13"
                />
                <circle
                  cx="74"
                  cy="74"
                  r="63"
                  fill="none"
                  stroke={metrics.band.badgeColor}
                  strokeWidth="13"
                  strokeLinecap="round"
                  strokeDasharray={SVG_CIRCUMFERENCE.toFixed(1)}
                  strokeDashoffset={strokeDashoffset.toFixed(1)}
                  style={{ transition: 'stroke-dashoffset 0.4s ease, stroke 0.4s ease' }}
                />
              </svg>
              <div className="radar-dial-center">
                <span className="radar-dial-num" style={{ color: metrics.band.badgeColor }}>
                  {metrics.total}
                </span>
                <span className="radar-dial-sub">trên 100 điểm</span>
              </div>
            </div>

            {/* Thông tin chẩn đoán sơ bộ */}
            <div>
              <span className={`radar-band-pill ${metrics.band.cls}`}>
                <i></i>
                <span>{metrics.noSalary ? 'Thiếu dữ liệu' : metrics.band.tag}</span>
              </span>

              <div className="radar-vtitle">
                {metrics.noSalary
                  ? 'Chưa chấm được chính xác — bạn chưa tính công của mình'
                  : portrait.tagline}
              </div>

              <div className="radar-vsub">
                {metrics.noSalary
                  ? 'Đang để chi phí nhân sự bằng 0. Công đứng quầy của bạn là chi phí thật; chưa tính vào thì con số lợi nhuận là ảo. Hãy nhập mức lương bạn phải trả nếu thuê người làm thay.'
                  : fillPortraitTokens(portrait.mota, metrics)}
              </div>

              <div className="radar-portrait-chip">
                <b>Chân Dung</b>
                <span>{portrait.ten}</span>
              </div>
            </div>
          </div>

          {/* 3–4 Gạch đầu dòng nhận định then chốt */}
          <ul className="radar-freelist">
            {metrics.noSalary && (
              <li>
                <span className="dot" style={{ background: 'var(--ttp-warn)' }}></span>
                <span>
                  Điểm đang bị giới hạn vì <strong>chi phí nhân sự đang để 0</strong>. Hãy nhập mức lương công đứng quầy
                  của bạn để có kết quả thực tế.
                </span>
              </li>
            )}

            <li>
              <span
                className="dot"
                style={{ background: metrics.ln >= 0 ? 'var(--ttp-good)' : 'var(--ttp-bad)' }}
              ></span>
              <span>
                {metrics.ln >= 0 ? (
                  <>
                    Lợi nhuận ước tính khoảng <strong>{fmtTrieu(metrics.ln)}/tháng</strong>, tương đương{' '}
                    <strong>{fmtPercent(metrics.bienLN)}</strong> doanh thu.
                  </>
                ) : (
                  <>
                    Quầy đang <strong>âm khoảng {fmtTrieu(Math.abs(metrics.ln))}/tháng</strong> sau khi tính đủ chi phí
                    và công sức của bạn.
                  </>
                )}
              </span>
            </li>

            <li>
              <span
                className="dot"
                style={{
                  background:
                    metrics.ngayTon > 90
                      ? 'var(--ttp-bad)'
                      : metrics.ngayTon > 70 || metrics.ngayTon < 25
                        ? 'var(--ttp-warn)'
                        : 'var(--ttp-good)',
                }}
              ></span>
              <span>
                Hàng trong kho đủ bán trong <strong>{Math.round(metrics.ngayTon)} ngày</strong> (quay vòng{' '}
                <strong>{fmtNumber(metrics.vongQuay, 1)} vòng/năm</strong>).
              </span>
            </li>

            <li>
              <span
                className="dot"
                style={{
                  background:
                    metrics.dem < 10 ? 'var(--ttp-bad)' : metrics.dem < 22 ? 'var(--ttp-warn)' : 'var(--ttp-good)',
                }}
              ></span>
              <span>
                {isFinite(metrics.dtHoaVon) ? (
                  <>
                    Phải bán tối thiểu <strong>{fmtTrieu(metrics.hvNgay)}/ngày</strong> mới chạm điểm hòa vốn — hiện bạn
                    đang bán <strong>{fmtTrieu(metrics.d.ds)}/ngày</strong>.
                  </>
                ) : (
                  <>
                    Biên lãi gộp quá thấp nên quầy <strong>không có điểm hòa vốn</strong> với mức chi phí cố định hiện tại.
                  </>
                )}
              </span>
            </li>
          </ul>
        </div>

        {/* LEAD GATE: FORM ĐĂNG KÝ NHẬN BÁO CÁO CHI TIẾT */}
        {!isUnlocked ? (
          <div className="radar-gate">
            <span className="radar-gate-badge">
              <UnlockOutlined /> Báo Cáo Phân Tích Chuyên Sâu
            </span>
            <h2 className="radar-gate-title">
              Đăng ký nhận báo cáo chi tiết & 5 hạng mục tư vấn riêng cho quầy của bạn
            </h2>
            <p className="radar-gate-desc">
              Bạn đã biết điểm số và chân dung quầy. Vui lòng nhập thông tin quầy để mở toàn bộ báo cáo chi tiết,
              trong đó có cẩm nang hành động được biên soạn riêng cho đúng nhóm quầy của bạn:
            </p>

            <ul className="radar-gate-perks">
              <li>
                <CheckCircleOutlined />
                <span>
                  <strong>Phần 1: 6 Chỉ số tài chính quan trọng</strong> (so sánh benchmark chuẩn ngành)
                </span>
              </li>
              <li>
                <CheckCircleOutlined />
                <span>
                  <strong>Phần 2: Cần lưu ý quan trọng</strong> (phát hiện chính xác chỗ đang rò rỉ dòng tiền)
                </span>
              </li>
              <li>
                <CheckCircleOutlined />
                <span>
                  <strong>Phần 3: Bảng lãi lỗ (P&L) chi tiết cho tháng</strong>
                </span>
              </li>
              <li>
                <CheckCircleOutlined />
                <span>
                  <strong>Phần 4: 4 Đòn bẩy quan trọng nhất</strong> (sắp xếp theo số tiền tạo thêm)
                </span>
              </li>
              <li>
                <CheckCircleOutlined />
                <span>
                  <strong>Phần 5: Bộ tư vấn riêng biệt cho quầy bạn</strong> (kế hoạch 7 ngày, 30 ngày, cơ cấu hàng)
                </span>
              </li>
            </ul>

            <form onSubmit={handleUnlockReport}>
              <div className="radar-gate-form">
                <div className="full-col">
                  <input
                    className="radar-gate-input"
                    type="text"
                    placeholder="Tên quầy thuốc / nhà thuốc *"
                    value={leadForm.shop}
                    onChange={(e) => setLeadForm({ ...leadForm, shop: e.target.value })}
                  />
                </div>

                <div>
                  <input
                    className="radar-gate-input"
                    type="text"
                    placeholder="Tên chủ quầy hoặc người phụ trách *"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  />
                </div>

                <div>
                  <input
                    className="radar-gate-input"
                    type="tel"
                    inputMode="numeric"
                    placeholder="Số điện thoại / Zalo *"
                    value={leadForm.phone}
                    onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                  />
                </div>

                <div>
                  <select
                    className="radar-gate-select"
                    value={leadForm.province}
                    onChange={handleProvinceChange}
                  >
                    <option value="">Chọn Tỉnh / Thành phố *</option>
                    {PROVINCES.map((p) => (
                      <option key={p.code} value={p.name}>
                        {p.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <select
                    className="radar-gate-select"
                    value={leadForm.district}
                    onChange={(e) => setLeadForm({ ...leadForm, district: e.target.value })}
                    disabled={!leadForm.province || wardOptions.length === 0}
                  >
                    <option value="">
                      {!leadForm.province
                        ? 'Chọn Tỉnh / Thành trước *'
                        : wardOptions.length === 0
                          ? 'Không có danh mục phường/xã'
                          : 'Chọn Phường / Xã *'}
                    </option>
                    {wardOptions.map((w) => (
                      <option key={w.code} value={w.fullName}>
                        {w.fullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="full-col">
                  <input
                    className="radar-gate-input"
                    type="text"
                    placeholder="Địa chỉ cụ thể (số nhà, tên đường...) *"
                    value={leadForm.address}
                    onChange={(e) => setLeadForm({ ...leadForm, address: e.target.value })}
                  />
                </div>

                {formError && (
                  <div className="full-col" style={{ color: 'var(--ttp-bad)', fontSize: 13.5, fontWeight: 600 }}>
                    {formError}
                  </div>
                )}

                <div className="full-col">
                  <button type="submit" className="radar-btn-primary" disabled={isSubmittingLead}>
                    <UnlockOutlined />
                    {isSubmittingLead ? 'Đang kích hoạt...' : 'Mở Báo Cáo Chi Tiết & Nhận 5 Mục Tư Vấn'}
                  </button>
                </div>
              </div>
            </form>

            <div className="radar-gate-microtrust">
              <SafetyCertificateOutlined />
              <span>
                Thông tin chỉ dùng để gửi tài liệu và hỗ trợ tư vấn khi bạn cần. Không spam, không chia sẻ cho bên thứ ba.
              </span>
            </div>
          </div>
        ) : (
          <div className="radar-unlocked-banner" style={{ flexWrap: 'wrap', gap: 12 }}>
            <div>
              <CheckCircleOutlined style={{ marginRight: 8, color: 'var(--ttp-teal)' }} />
              <strong>Đã mở khóa báo cáo đầy đủ cho:</strong> {leadForm.shop} — {leadForm.name} ({leadForm.phone}) · {leadForm.district ? `${leadForm.district}, ` : ''}{leadForm.province}
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--ttp-teal)',
                  color: 'var(--ttp-teal)',
                  padding: '4px 12px',
                  borderRadius: 6,
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
                onClick={() => setIsUnlocked(false)}
              >
                Chỉnh sửa thông tin
              </button>
            </div>
          </div>
        )}

        {/* TOÀN BỘ BÁO CÁO CHUYÊN SÂU 5 PHẦN (Hiển thị sau khi mở khóa) */}
        {isUnlocked && (
          <div id="detailed-report" ref={reportRef} style={{ animation: 'fadeIn 0.4s ease' }}>
            {/* PHẦN 1: SÁU CHỈ SỐ BENCHMARK CỦA QUẦY */}
            <div className="radar-section-head">
              <span className="radar-section-eyebrow">Phần 1</span>
              <h2 className="radar-section-title">Sáu chỉ số của quầy so với chuẩn ngành</h2>
              <p className="radar-section-desc">
                Vạch đen là vị trí quầy bạn. Dải màu là ngưỡng tham chiếu chuẩn của quầy thuốc bán lẻ.
              </p>
            </div>

            <div className="radar-panel">
              {benchmarkMetrics.map((item) => (
                <div key={item.id} className="radar-metric-card">
                  <div className="radar-metric-name">{item.name}</div>
                  <div className="radar-metric-val">{item.val}</div>

                  <div className="radar-metric-bar">
                    <div className="radar-track">
                      <div className="zones">
                        {item.zones.map((z, idx) => (
                          <span key={idx} style={{ width: `${z.width}%`, background: z.color }}></span>
                        ))}
                      </div>
                      <div className="radar-marker" style={{ left: `${item.pos.toFixed(1)}%` }}></div>
                    </div>
                    <div className="radar-scale">
                      {item.labels.map((lbl, idx) => (
                        <span key={idx}>{lbl}</span>
                      ))}
                    </div>
                  </div>

                  <div className="radar-metric-note">{item.note}</div>
                </div>
              ))}
            </div>

            {/* PHẦN 2: CẦN LƯU Ý QUAN TRỌNG (ALERTS) */}
            <div className="radar-section-head">
              <span className="radar-section-eyebrow">Phần 2</span>
              <h2 className="radar-section-title">Các điểm nghẽn & lưu ý quan trọng</h2>
              <p className="radar-section-desc">Cảnh báo động bóc tách đúng vị trí đang rò rỉ tiền tại quầy bạn.</p>
            </div>

            <div className="radar-alerts-list">
              {alertsData.map((alert, idx) => (
                <div key={idx} className={`radar-alert-item a-${alert.type}`}>
                  <span className="radar-alert-ic">{alert.icon}</span>
                  <div>
                    <h4>{alert.title}</h4>
                    <p>{alert.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* PHẦN 3: BẢNG LÃI LỖ TÀI CHÍNH CHI TIẾT (P&L) */}
            <div className="radar-section-head">
              <span className="radar-section-eyebrow">Phần 3</span>
              <h2 className="radar-section-title">Bảng lãi lỗ tài chính chi tiết 1 tháng (P&L)</h2>
              <p className="radar-section-desc">Ước tính theo số liệu bạn vừa nhập, đơn vị: triệu đồng.</p>
            </div>

            <div className="radar-panel" style={{ overflowX: 'auto' }}>
              <table className="radar-pnl-table">
                <tbody>
                  {pnlData.map((row, idx) => {
                    const valText = row.val !== null ? fmtNumber(Math.abs(row.val), 1) : '—';
                    let displayVal = valText;
                    if (row.val !== null && row.isNegative) {
                      displayVal = `– ${valText}`;
                    } else if (row.val !== null && row.isSum && row.val > 0) {
                      displayVal = `+ ${valText}`;
                    }

                    return (
                      <tr
                        key={idx}
                        className={`${row.isSum ? 'sum' : ''} ${row.isFinal ? 'final-sum' : ''}`}
                      >
                        <th>{row.title}</th>
                        <td
                          style={{
                            color: row.isFinal
                              ? row.isNegative
                                ? 'var(--ttp-bad)'
                                : 'var(--ttp-good)'
                              : row.isHighlight
                                ? 'var(--ttp-teal-dark)'
                                : 'inherit',
                          }}
                        >
                          {displayVal}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* PHẦN 4: BỐN ĐÒN BẨY QUAN TRỌNG NHẤT CẦN LÀM */}
            <div className="radar-section-head">
              <span className="radar-section-eyebrow">Phần 4</span>
              <h2 className="radar-section-title">Bốn đòn bẩy tài chính quan trọng, xếp theo tiền</h2>
              <p className="radar-section-desc">
                Nếu chỉ có đủ nguồn lực để làm một việc trong tháng này, hãy tập trung làm việc số 1.
              </p>
            </div>

            <div className="radar-panel">
              {leversData.top4.map((lever, idx) => (
                <div key={idx} className="radar-lever-item">
                  <span className="radar-rank-badge">{idx + 1}</span>
                  <div>
                    <div className="radar-lever-name">{lever.name}</div>
                    <div className="radar-lever-desc">{lever.desc}</div>
                  </div>
                  <div className="radar-lever-val">
                    +{fmtNumber(lever.val, 1)} {lever.unit}
                  </div>
                </div>
              ))}

              {/* Bonus Đòn bẩy giải phóng tiền mặt từ tồn kho */}
              {leversData.cashTraiVe && (
                <div className="radar-lever-item" style={{ background: '#fffbeb' }}>
                  <span
                    className="radar-rank-badge"
                    style={{ background: 'var(--ttp-warn-bg)', color: 'var(--ttp-warn)' }}
                  >
                    ₫
                  </span>
                  <div>
                    <div className="radar-lever-name" style={{ color: 'var(--ttp-warn-text)' }}>
                      Kéo số ngày tồn kho về 60 ngày
                    </div>
                    <div className="radar-lever-desc">
                      Không làm tăng lợi nhuận trên giấy nhưng lập tức giải phóng tiền mặt đang bị giam chết trên kệ.
                    </div>
                  </div>
                  <div className="radar-lever-val" style={{ color: 'var(--ttp-warn-text)' }}>
                    +{fmtNumber(leversData.cashTraiVe, 0)} tr tiền mặt
                  </div>
                </div>
              )}
            </div>

            {/* PHẦN 5: BỘ TƯ VẤN RIÊNG CHO QUẦY CỦA BẠN (THEO CHÂN DUNG) */}
            <div className="radar-section-head">
              <span className="radar-section-eyebrow">Phần 5</span>
              <h2 className="radar-section-title">Bộ tư vấn & Cẩm nang hành động riêng cho quầy</h2>
              <p className="radar-section-desc">
                Biên soạn sẵn cho đúng nhóm quầy mà tình trạng thực tế của bạn rơi vào.
              </p>
            </div>

            {/* Banner chân dung */}
            <div className="radar-portrait-banner">
              <div className="radar-portrait-label">Chân dung quầy của bạn · Mã {portrait.id}</div>
              <h2 className="radar-portrait-h2">{portrait.ten}</h2>
              <div className="radar-portrait-tagline">"{portrait.tagline}"</div>
              <div className="radar-portrait-mota">{fillPortraitTokens(portrait.mota, metrics)}</div>
            </div>

            {/* 5 Hạng mục tư vấn chi tiết */}
            <div className="radar-panel" style={{ marginTop: 16 }}>
              {portrait.hangmuc.map((hm, idx) => (
                <div key={idx} className="radar-hm-card">
                  <div className="radar-hm-num">
                    <b>{idx + 1}</b> Hạng mục {idx + 1}
                  </div>
                  <h3 className="radar-hm-title">{hm.t}</h3>

                  {hm.p && <p className="radar-hm-desc">{fillPortraitTokens(hm.p, metrics)}</p>}

                  {hm.b && hm.b.length > 0 && (
                    <ul className="radar-hm-list">
                      {hm.b.map((point, bIdx) => (
                        <li key={bIdx}>
                          <span>{fillPortraitTokens(point, metrics)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* PHẦN 6: CALL TO ACTION (CAT / CTA) */}
            <div className="radar-cta-box">
              <h2 className="radar-cta-title">Cộng Đồng Nhà Thuốc & Dược Sĩ 5.0</h2>
              <p className="radar-cta-sub">
                Tham gia nhóm Facebook để giao lưu cùng các chủ quầy thuốc, học hỏi kinh nghiệm quản trị, vận hành thực chiến và nhận giải đáp chuyên môn từ đội ngũ Dược sĩ Thế Thảo Pharma.
              </p>

              <div className="radar-cta-buttons">
                <a
                  href="https://www.facebook.com/groups/934460299578977"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="radar-cta-btn btn-white"
                  style={{ maxWidth: 380, fontSize: 15.5, padding: '13px 24px' }}
                >
                  <TeamOutlined style={{ fontSize: 18 }} /> Vào cộng đồng Dược Sĩ 5.0
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Footer thông tin & Disclaimer */}
        <footer className="radar-footer">
          <div>
            <strong>Thế Thảo Pharma</strong> — Đơn vị phân phối và đồng hành cùng nhà thuốc Việt — 60B Nguyễn Huy Tưởng, Thanh Xuân, Hà Nội · Hotline / Zalo:{' '}
            <strong>0812.539.539</strong> · Website: chothuoc247.vn
          </div>
          <div className="disclaimer">
            <strong>Lưu ý:</strong> Toàn bộ chỉ số, điểm số và nhận định trong báo cáo này được tính toán tự động dựa
            trên số liệu bạn tự nhập và các ngưỡng tham chiếu phổ biến của ngành dược phẩm bán lẻ tại Việt Nam. Các
            chỉ số chỉ mang tính chất tham khảo, không thay thế cho sổ sách kế toán thuế thực tế hoặc tư vấn chuyên
            môn trực tiếp. Chủ quầy nên đối chiếu với số liệu thực tế của mình trước khi đưa ra quyết định kinh doanh.
          </div>
        </footer>
      </div>

      {/* Nút nổi xuất PDF - luôn bám theo màn hình khi cuộn vì báo cáo khá dài */}
      {isUnlocked && (
        <Tooltip title="Xuất PDF Báo Cáo" placement="right">
          <button type="button" className="radar-pdf-fab" onClick={handleExportPdf} aria-label="Xuất PDF Báo Cáo">
            <FilePdfOutlined />
          </button>
        </Tooltip>
      )}
    </div>
  );
}
